// Control room (static demo pages/ControlRoom.jsx, screen 6) on real data: a
// full-screen wall view cycling through the chain (Swiss map + open alarms),
// all open alarms, and each store with a problem. Without floor plans (a later
// project) a store scene shows its cabinets and rooms as tiles. Esc or the
// exit button returns to the overview.
import { useEffect, useState } from "preact/hooks";
import { FutureFridgesMark } from "../components/Logo.jsx";
import SwissMap from "../components/SwissMap.jsx";
import { useT } from "../i18n";
import { accounts, current } from "../lib/account";
import { go } from "../lib/nav";
import { ALARM_ST, useFleet } from "../data/fleet";
import { clsName, inRange } from "../data/classes";
import { rollup, sites, type LocNode } from "../data/locations";
import { LABEL_POS } from "./Overview";
import { State } from "./common";

const SCENE_S = 12;
const ORDER: Record<string, number> = { crit: 0, alarm: 1, warn: 2, delayed: 3, ok: 4 };
const sev = (type: string) => ALARM_ST[type] || "warn";

export default function ControlRoom() {
  const t = useT();
  const f = useFleet(t.lang);
  const [now, setNow] = useState(Date.now());
  const [i, setI] = useState(0);
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  useEffect(() => { const k = (e: KeyboardEvent) => e.key === "Escape" && go("/"); addEventListener("keydown", k); return () => removeEventListener("keydown", k); }, []);
  const vis = f ? sites(f.root) : [];
  const trouble = vis.filter((s) => s.state && ["crit", "alarm", "warn"].includes(s.state.st)).sort((a, b) => ORDER[a.state!.st] - ORDER[b.state!.st]).slice(0, 4);
  const scenes = ["chain", "alarms", ...trouble.map((s) => s.id)];
  useEffect(() => { const id = setInterval(() => setI((x) => (x + 1) % scenes.length), SCENE_S * 1000); return () => clearInterval(id); }, [scenes.length]);
  if (!f) return <div class="control"><State loading /></div>;
  const scene = scenes[i % scenes.length];
  const alarms = [...f.alarms].sort((a, b) => ORDER[sev(a.type)] - ORDER[sev(b.type)] || Date.parse(b.utc) - Date.parse(a.utc));
  const siteOf = new Map<string, LocNode>();
  vis.forEach((s) => s.points.forEach((p) => siteOf.set(p.id, s)));
  const rated = f.points.filter((p) => p.regime && p.temp != null);
  const inR = rated.length ? (rated.filter((p) => inRange(p.regime, p.temp!)).length / rated.length) * 100 : null;
  const bad = vis.filter((s) => s.state?.st === "crit" || s.state?.st === "alarm").length;
  const cities = f.root.children.filter((c) => c.map).map((c) => { const rr = rollup(c); return { id: c.id, name: c.name, x: c.map!.x, y: c.map!.y, count: rr.sites, worst: rr.worst, ...(LABEL_POS[c.value] || {}) }; });
  const mins = (utc: string) => Math.max(0, Math.round((now - Date.parse(utc)) / 60e3));
  const what = (a: (typeof alarms)[number]) => { const p = f.byId.get(a.device_id); return sev(a.type) === "delayed" ? `${t("x.at.NO_DATA")} · ${p?.name || a.device_serial}` : `${p?.name || a.device_serial} · ${t.temp(p?.temp ?? null)}`; };
  const site = scene.startsWith("chain") || scene === "alarms" ? null : vis.find((s) => s.id === scene) || null;
  return (
    <div class="control">
      <header>
        <div class="row" style="gap:14px"><FutureFridgesMark size={40} /><div><b style="font-size:20px;letter-spacing:.12em;font-family:var(--f-disp)">FutureFridges Sentinel</b><div style="color:var(--ai-dim);font-size:13px">{t("nav.control")} · {current.value?.name || accounts.value.find((a) => !a.parent_id)?.name || ""}</div></div></div>
        <div class="row" style="gap:28px">
          <div class="cbig"><span>{t("kpi.storesAlarm")}</span><b style={{ color: bad ? "var(--crit)" : undefined }}>{bad}</b></div>
          <div class="cbig"><span>{t("nav.alarms")}</span><b>{alarms.length}</b></div>
          <div class="cbig"><span>{t("kpi.inRange")}</span><b>{inR == null ? "—" : `${t.num(Math.round(inR * 10) / 10)}%`}</b></div>
          <div class="clock">{new Date(now).toLocaleTimeString(t.locale)}</div>
          <button class="btn" type="button" onClick={() => go("/")}>{t("ctl.exit")}</button>
        </div>
      </header>
      <div class="cscene" key={scene}>
        {scene === "chain" && <div class="grid" style="grid-template-columns:minmax(0,1.5fr) minmax(0,1fr);align-items:center">
          <div class="card" style="background:transparent;border:0;box-shadow:none"><SwissMap cities={cities} /></div>
          <div class="grid">{alarms.length === 0 && <div class="cal"><b>{t("dash.allClear")}</b></div>}
            {alarms.slice(0, 4).map((a) => <div key={a.id} class={"cal " + sev(a.type)}><span class={"st st-" + sev(a.type)}>{t("st." + sev(a.type))}</span><b>{siteOf.get(a.device_id)?.name || f.byId.get(a.device_id)?.name || a.device_serial}</b><span>{what(a)}</span><span class="mono">{t.dur(mins(a.utc))}</span></div>)}</div>
        </div>}
        {scene === "alarms" && <div class="grid" style="grid-template-columns:repeat(2,minmax(0,1fr))">
          {alarms.length === 0 && <div class="cal big"><b style="font-size:28px">{t("dash.allClear")}</b></div>}
          {alarms.slice(0, 8).map((a) => <div key={a.id} class={"cal big " + sev(a.type)}>
            <div class="row" style="justify-content:space-between"><span class={"st st-" + sev(a.type)}>{t("st." + sev(a.type))}</span><span class="mono" style="font-size:22px">{t.dur(mins(a.utc))}</span></div>
            <b style="font-size:28px">{what(a)}</b>
            <span style="font-size:18px">{siteOf.get(a.device_id)?.name || ""}{siteOf.get(a.device_id) ? " · " : ""}{a.ack_utc ? t("note.acknowledged") : t("ctl.waiting")}</span>
          </div>)}
        </div>}
        {site && (() => {
          const pts = [...site.points].sort((a, b) => ORDER[a.st] - ORDER[b.st] || a.name.localeCompare(b.name));
          const worst = pts[0];
          return <div>
            <h2 style="margin:0 0 12px;font-size:30px">{site.name} <span style="color:var(--ai-dim);font-size:18px;font-weight:400">· {worst?.name} {t.temp(worst?.temp ?? null)}</span></h2>
            <div class="cabs">{pts.map((o) => <div key={o.id} class={"cab " + (o.st === "crit" ? "crit" : o.st === "warn" ? "warn" : o.st === "alarm" ? "alarm" : "")}>
              <div class="h"><b>{o.name}</b><span class={"st st-" + o.st}>{t("st." + o.st)}</span></div>
              <div class="temp">{t.temp(o.temp)}</div>
              <small>{o.regime ? clsName(t.lang, o.regime) : ""}</small>
            </div>)}</div>
          </div>;
        })()}
      </div>
      <footer><div class="cprog"><i style={{ animationDuration: SCENE_S + "s" }} key={i} /></div><div class="row" style="gap:8px">{scenes.map((s, k) => <button key={s} type="button" class={"cdot" + (k === i % scenes.length ? " on" : "")} onClick={() => setI(k)} aria-label={String(k + 1)} />)}</div></footer>
    </div>
  );
}
